import { isEmpty } from "../../functions/alert";
import { invalidName } from "../../functions/general";

/**
 * New API contract: settings, question, answer, recommendations, solution.
 */
export function buildTaskPayload(task) {
  const tag_id = [...new Set([...(task.domains ?? []), ...(task.tags ?? [])])];
  const mainLang = task.languages?.[0] ?? {};
  const settings = {
    title: task.title ?? "",
    identifier: task.identifier ?? "",
    tag_id,
    no_translation_needed: Boolean(task.noTranslationNeeded),
  };
  const question = {
    label: mainLang.task_title ?? "",
    content: mainLang.content ?? "",
  };

  const type = task.answer_type;
  let answer;
  if (type === "mcq") {
    const choices = (task.task_answers ?? []).map((a) => ({
      content: a.task_labels?.[0]?.content ?? "",
      is_correct: Boolean(a.correct),
    }));
    answer = { type: "mcq", choices };
  } else if (type === "open_ended") {
    const rows = task.task_answers ?? [];
    answer = {
      type: "open_ended",
      label: rows[0]?.task_labels?.[0]?.content ?? "",
    };
  } else {
    const rows = task.task_answers ?? [];
    const correct_answer =
      rows.length <= 1
        ? String(rows[0]?.answer ?? "")
        : rows
            .map((r) => r.answer)
            .filter((s) => s !== "" && s != null)
            .join(", ");
    const label = rows[0]?.task_labels?.[0]?.content ?? "";
    answer = { type: "input", correct_answer, label };
  }

  const recommendations = (task.grade_difficulty ?? [])
    .filter(
      (r) =>
        r.difficulty &&
        r.grade !== "" &&
        r.grade !== null &&
        r.grade !== undefined
    )
    .map((r) => {
      const grade = typeof r.grade === "number" ? r.grade : Number(r.grade);
      return {
        grade,
        difficulty: String(r.difficulty).toLowerCase(),
      };
    })
    .filter((r) => Number.isFinite(r.grade));

  return {
    settings,
    question,
    answer,
    recommendations,
    solution: task.solutions ?? "",
  };
}

/**
 * @returns {string | null} First validation error message, or null if valid.
 */
export function getTaskPayloadValidationError(task) {
  if (invalidName(task.title)) return "Invalid task title.";
  if (invalidName(task.identifier)) return "Invalid task identifier.";
  const content = task.languages?.[0]?.content;
  if (isEmpty([content])) return "Task content cannot be empty.";

  const type = task.answer_type;
  if (type === "mcq") {
    const choices = task.task_answers ?? [];
    if (choices.length === 0) return "MCQ must have at least one choice.";
    if (!choices.some((c) => c.correct))
      return "Select exactly one correct answer for MCQ.";
  } else if (type === "input") {
    const rows = task.task_answers ?? [];
    if (!String(rows[0]?.task_labels?.[0]?.content ?? "").trim())
      return "Answer label is required for input type.";
    if (rows.some((a) => (a.answer ?? "").trim() === ""))
      return "Fill in the correct answer for each input field.";
  }
  return null;
}
